import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  booleanAttribute,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsAccordionComponent } from '../containment/ds-accordion.component';
import { DsIconComponent } from '../icons/ds-icon.component';
import type { DsIconName } from '../icons/ds-icons';
import { DsScrollComponent } from '../styles/ds-scroll';
import {
  DsTagPillComponent,
  type DsTagPillVariant,
} from '../tags/ds-tag-pill.component';

export interface DsSideNavigationTag {
  label: string;
  variant?: DsTagPillVariant;
  invert?: boolean;
}

export type DsSideNavigationTarget = '_self' | '_blank' | '_parent' | '_top';

export interface DsSideNavigationChild {
  label: string;
  value: string;
  href?: string;
  target?: DsSideNavigationTarget;
  disabled?: boolean;
  ariaLabel?: string;
  tag?: DsSideNavigationTag;
}

export interface DsSideNavigationItem {
  label: string;
  value: string;
  icon: DsIconName | string;
  href?: string;
  target?: DsSideNavigationTarget;
  disabled?: boolean;
  ariaLabel?: string;
  tag?: DsSideNavigationTag;
  children?: readonly DsSideNavigationChild[];
}

export type DsSideNavigationBreakpoint = 'desktop' | 'mobile';
export type DsSideNavigationSelection = DsSideNavigationItem | DsSideNavigationChild;

export interface DsSideNavigationContactContext {
  close: () => void;
}

export interface DsSideNavigationContactTemplateContext extends DsSideNavigationContactContext {
  $implicit: DsSideNavigationContactContext;
}

@Directive({
  selector: 'ng-template[dsSideNavigationContactContent]',
  standalone: true,
})
export class DsSideNavigationContactContentDirective {
  constructor(readonly templateRef: TemplateRef<DsSideNavigationContactTemplateContext>) {}

  static ngTemplateContextGuard(
    _directive: DsSideNavigationContactContentDirective,
    _context: unknown,
  ): _context is DsSideNavigationContactTemplateContext {
    return true;
  }
}

@Component({
  selector: 'ds-side-navigation',
  standalone: true,
  imports: [
    CdkTrapFocus,
    DsIconButtonV2Component,
    DsIconComponent,
    DsScrollComponent,
    DsTagPillComponent,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ds-side-navigation-shell"
      [class.ds-side-navigation-shell--desktop]="!isMobile"
      [class.ds-side-navigation-shell--mobile]="isMobile"
      [class.ds-side-navigation-shell--open]="isMobile && open"
      [attr.aria-hidden]="isMobile && !open ? 'true' : null"
    >
      @if (isMobile) {
        <div
          class="ds-side-navigation__backdrop"
          aria-hidden="true"
          (click)="requestClose()"
        ></div>
      }

      <section
        class="ds-side-navigation__panel"
        cdkTrapFocus
        [cdkTrapFocus]="isMobile && open"
        [cdkTrapFocusAutoCapture]="isMobile && open"
        [attr.role]="isMobile ? 'dialog' : null"
        [attr.aria-modal]="isMobile && open ? 'true' : null"
        [attr.aria-label]="isMobile ? mobileTitle : null"
      >
        @if (isMobile) {
          <header class="ds-side-navigation__mobile-header">
            <span class="ds-side-navigation__mobile-header-start">
              @if (contactOpen) {
                <ds-icon-button-v2
                  emphasis="secondary"
                  size="xs"
                  icon="ArrowBack"
                  [ariaLabel]="contactBackAriaLabel"
                  (clicked)="closeContact()"
                />
              }
              <span class="ds-side-navigation__mobile-title ds-text--visual-padding-none">
                {{ contactOpen ? contactPanelTitle : mobileTitle }}
              </span>
            </span>
            <span class="ds-side-navigation__mobile-close">
              <ds-icon-button-v2
                emphasis="secondary"
                size="xs"
                icon="Close"
                [ariaLabel]="closeAriaLabel"
                (clicked)="requestClose()"
              />
            </span>
          </header>
        }

        <nav
          dsScroll
          class="ds-side-navigation"
          [class.ds-side-navigation--contact-open]="isMobile && contactOpen"
          [attr.aria-label]="ariaLabel"
          [attr.aria-disabled]="disabled || null"
        >
          @if (isMobile && contactOpen) {
            <div class="ds-side-navigation__contact">
              <div class="ds-side-navigation__contact-panel">
                @if (contactContent) {
                  <ng-container
                    [ngTemplateOutlet]="contactContent.templateRef"
                    [ngTemplateOutletContext]="contactTemplateContext"
                  />
                }
              </div>
            </div>
          } @else {
          <ul class="ds-side-navigation__list">
            @for (item of items; track item.value) {
              <li class="ds-side-navigation__group">
                @if (item.href && !hasChildren(item)) {
                  <a
                    class="ds-side-navigation__item"
                    [class.ds-side-navigation__item--active]="isItemActive(item) && !disabled && !item.disabled"
                    [class.ds-side-navigation__item--disabled]="disabled || item.disabled"
                    [attr.href]="disabled || item.disabled ? null : item.href"
                    [attr.target]="disabled || item.disabled ? null : item.target || null"
                    [attr.rel]="disabled || item.disabled ? null : linkRel(item.target)"
                    [attr.tabindex]="disabled || item.disabled ? -1 : null"
                    [attr.aria-disabled]="disabled || item.disabled ? 'true' : null"
                    [attr.aria-label]="item.ariaLabel || null"
                    [attr.aria-current]="isCurrentItem(item) ? 'page' : null"
                    [attr.data-ds-side-navigation-value]="item.value"
                    (click)="selectItem(item)"
                  >
                    <ds-icon
                      class="ds-side-navigation__leading-icon"
                      aria-hidden="true"
                      [name]="item.icon"
                      [size]="'var(--ds-side-navigation-icon-size)'"
                    />
                    <span class="ds-side-navigation__label ds-text--visual-padding-none">
                      {{ item.label }}
                    </span>
                    @if (item.tag) {
                      <ds-tag-pill
                        [label]="item.tag.label"
                        [variant]="item.tag.variant || 'new'"
                        [size]="'xs'"
                        [invert]="item.tag.invert || false"
                      />
                    }
                  </a>
                } @else {
                <button
                  class="ds-side-navigation__item"
                  type="button"
                  [class.ds-side-navigation__item--active]="isItemActive(item) && !isExpanded(item) && !disabled && !item.disabled"
                  [class.ds-side-navigation__item--expanded]="isExpanded(item) && !disabled && !item.disabled"
                  [disabled]="disabled || item.disabled || null"
                  [attr.aria-label]="item.ariaLabel || null"
                  [attr.aria-current]="isCurrentItem(item) ? 'page' : null"
                  [attr.aria-expanded]="hasChildren(item) ? isExpanded(item) : null"
                  [attr.aria-controls]="hasChildren(item) ? submenuId(item) : null"
                  [attr.data-ds-side-navigation-value]="item.value"
                  (click)="selectItem(item)"
                >
                  <ds-icon
                    class="ds-side-navigation__leading-icon"
                    aria-hidden="true"
                    [name]="item.icon"
                    [size]="'var(--ds-side-navigation-icon-size)'"
                  />
                  <span class="ds-side-navigation__label ds-text--visual-padding-none">
                    {{ item.label }}
                  </span>
                  @if (item.tag) {
                    <ds-tag-pill
                      [label]="item.tag.label"
                      [variant]="item.tag.variant || 'new'"
                      [size]="'xs'"
                      [invert]="item.tag.invert || false"
                    />
                  }
                  @if (hasChildren(item)) {
                    <ds-icon
                      class="ds-side-navigation__chevron"
                      aria-hidden="true"
                      [name]="isExpanded(item) ? 'KeyboardArrowUp' : 'KeyboardArrowDown'"
                      [size]="'var(--ds-side-navigation-chevron-size)'"
                    />
                  }
                </button>
                }

                @if (hasChildren(item) && isExpanded(item)) {
                  <ul class="ds-side-navigation__submenu" [id]="submenuId(item)">
                    @for (child of item.children ?? []; track child.value) {
                      <li>
                        @if (child.href) {
                          <a
                            class="ds-side-navigation__subitem"
                            [class.ds-side-navigation__subitem--selected]="child.value === value && !disabled && !item.disabled && !child.disabled"
                            [class.ds-side-navigation__subitem--disabled]="disabled || item.disabled || child.disabled"
                            [attr.href]="disabled || item.disabled || child.disabled ? null : child.href"
                            [attr.target]="disabled || item.disabled || child.disabled ? null : child.target || null"
                            [attr.rel]="disabled || item.disabled || child.disabled ? null : linkRel(child.target)"
                            [attr.tabindex]="disabled || item.disabled || child.disabled ? -1 : null"
                            [attr.aria-disabled]="disabled || item.disabled || child.disabled ? 'true' : null"
                            [attr.aria-label]="child.ariaLabel || null"
                            [attr.aria-current]="child.value === value && !disabled && !item.disabled && !child.disabled ? 'page' : null"
                            [attr.data-ds-side-navigation-value]="child.value"
                            (click)="selectChild(item, child)"
                          >
                            @if (child.tag) {
                              <ds-tag-pill
                                [label]="child.tag.label"
                                [variant]="child.tag.variant || 'new'"
                                [size]="'xs'"
                                [invert]="child.tag.invert || false"
                              />
                            }
                            <span class="ds-side-navigation__label ds-text--visual-padding-none">
                              {{ child.label }}
                            </span>
                          </a>
                        } @else {
                        <button
                          class="ds-side-navigation__subitem"
                          type="button"
                          [class.ds-side-navigation__subitem--selected]="child.value === value && !disabled && !item.disabled && !child.disabled"
                          [disabled]="disabled || item.disabled || child.disabled || null"
                          [attr.aria-label]="child.ariaLabel || null"
                          [attr.aria-current]="child.value === value && !disabled && !item.disabled && !child.disabled ? 'page' : null"
                          [attr.data-ds-side-navigation-value]="child.value"
                          (click)="selectChild(item, child)"
                        >
                          @if (child.tag) {
                            <ds-tag-pill
                              [label]="child.tag.label"
                              [variant]="child.tag.variant || 'new'"
                              [size]="'xs'"
                              [invert]="child.tag.invert || false"
                            />
                          }
                          <span class="ds-side-navigation__label ds-text--visual-padding-none">
                            {{ child.label }}
                          </span>
                        </button>
                        }
                      </li>
                    }
                  </ul>
                }
              </li>
            }
          </ul>

          @if (isMobile && showContact && contactContent) {
            <hr class="ds-side-navigation__contact-divider" />
            <button
              type="button"
              class="ds-side-navigation__item ds-side-navigation__contact-link"
              [attr.aria-label]="contactAriaLabel"
              (click)="openContact()"
            >
              <ds-icon
                class="ds-side-navigation__leading-icon"
                aria-hidden="true"
                name="Forum"
                [size]="'var(--ds-side-navigation-icon-size)'"
              />
              <span class="ds-side-navigation__label ds-text--visual-padding-none">
                {{ contactLabel }}
              </span>
            </button>
          }

          <div class="ds-side-navigation__footer">
            <ng-content select="[dsSideNavigationFooter]" />
          </div>
          }
        </nav>
      </section>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: min(100%, var(--ds-side-navigation-width));
      height: 100%;
      max-width: 100%;
    }

    :host(.ds-side-navigation-host--mobile) {
      display: contents;
    }

    .ds-side-navigation-shell,
    .ds-side-navigation__panel {
      box-sizing: border-box;
    }

    .ds-side-navigation-shell--desktop,
    .ds-side-navigation-shell--desktop .ds-side-navigation__panel {
      width: 100%;
      height: 100%;
    }

    .ds-side-navigation-shell--mobile {
      position: fixed;
      z-index: var(--ds-side-navigation-mobile-z-index);
      inset: 0;
      visibility: hidden;
      pointer-events: none;
      transition: visibility 0s linear var(--ds-side-navigation-mobile-transition-duration);
    }

    .ds-side-navigation-shell--mobile.ds-side-navigation-shell--open {
      visibility: visible;
      pointer-events: auto;
      transition-delay: 0s;
    }

    .ds-side-navigation__backdrop {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      padding: 0;
      border: 0;
      background: var(--ds-side-navigation-mobile-scrim-color);
      backdrop-filter: blur(var(--ds-side-navigation-mobile-backdrop-blur));
      -webkit-backdrop-filter: blur(var(--ds-side-navigation-mobile-backdrop-blur));
      cursor: pointer;
      opacity: 0;
      transition: opacity var(--ds-side-navigation-mobile-transition-duration) ease-out;
    }

    .ds-side-navigation-shell--open .ds-side-navigation__backdrop {
      opacity: 1;
    }

    .ds-side-navigation-shell--mobile .ds-side-navigation__panel {
      position: absolute;
      inset-block: 0;
      right: 0;
      display: flex;
      width: min(100%, var(--ds-side-navigation-mobile-panel-width));
      min-height: 0;
      flex-direction: column;
      background: var(--ds-side-navigation-background);
      transform: translateX(100%);
      transition: transform var(--ds-side-navigation-mobile-transition-duration) cubic-bezier(0.22, 1, 0.36, 1);
    }

    .ds-side-navigation-shell--open .ds-side-navigation__panel {
      transform: translateX(0);
    }

    .ds-side-navigation__mobile-header {
      display: flex;
      width: 100%;
      min-height: var(--ds-side-navigation-mobile-header-height);
      flex: 0 0 var(--ds-side-navigation-mobile-header-height);
      align-items: center;
      justify-content: space-between;
      padding:
        var(--ds-side-navigation-mobile-header-padding-y)
        var(--ds-side-navigation-mobile-header-padding-x);
      border-bottom:
        var(--ds-side-navigation-mobile-header-border-width)
        solid
        var(--ds-side-navigation-mobile-header-border-color);
      background: var(--ds-side-navigation-background);
      box-sizing: border-box;
    }

    .ds-side-navigation__mobile-header-start {
      display: flex;
      min-width: 0;
      align-items: center;
      gap: var(--ds-side-navigation-item-content-gap);
    }

    .ds-side-navigation__mobile-title {
      min-width: 0;
      color: var(--ds-side-navigation-mobile-header-title-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-side-navigation-mobile-header-font-size);
      font-weight: var(--ds-side-navigation-mobile-header-font-weight);
      line-height: var(--ds-side-navigation-mobile-header-line-height);
    }

    .ds-side-navigation__mobile-close {
      display: flex;
      width: var(--ds-side-navigation-mobile-close-slot-width);
      height: var(--ds-side-navigation-mobile-close-slot-height);
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
    }

    .ds-side-navigation {
      display: flex;
      width: 100%;
      height: 100%;
      min-height: 0;
      flex-direction: column;
      overflow-y: auto;
      scrollbar-gutter: stable;
      padding-top: var(--ds-side-navigation-padding-top);
      padding-right: var(--ds-side-navigation-padding-end);
      border: 0;
      border-right: var(--ds-side-navigation-submenu-divider-width) solid var(--ds-side-navigation-border-color);
      background: var(--ds-side-navigation-background);
      box-sizing: border-box;
      color: var(--ds-side-navigation-content-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
    }

    .ds-side-navigation-shell--mobile .ds-side-navigation {
      flex: 1 1 auto;
      border-right: 0;
    }

    .ds-side-navigation-shell--mobile .ds-side-navigation--contact-open {
      padding-right: var(--ds-side-navigation-contact-navigation-padding-end);
    }

    .ds-side-navigation__list,
    .ds-side-navigation__submenu {
      padding: 0;
      margin: 0;
      list-style: none;
    }

    .ds-side-navigation__list {
      display: flex;
      flex-direction: column;
      gap: var(--ds-side-navigation-item-gap);
    }

    .ds-side-navigation__group {
      display: flex;
      min-width: 0;
      flex-direction: column;
    }

    .ds-side-navigation__item,
    .ds-side-navigation__subitem {
      position: relative;
      display: flex;
      width: 100%;
      min-width: 0;
      border: 0;
      background: none;
      box-sizing: border-box;
      color: var(--ds-side-navigation-content-color);
      cursor: pointer;
      font: inherit;
      font-size: var(--ds-side-navigation-font-size);
      line-height: var(--ds-side-navigation-line-height);
      outline: none;
      text-align: left;
      text-decoration: none;
      transition:
        background-color var(--ds-side-navigation-transition-duration) ease,
        color var(--ds-side-navigation-transition-duration) ease,
        opacity var(--ds-side-navigation-transition-duration) ease;
    }

    .ds-side-navigation__item {
      min-height: var(--ds-side-navigation-item-min-height);
      align-items: center;
      gap: var(--ds-side-navigation-item-content-gap);
      padding:
        var(--ds-side-navigation-item-padding-y)
        var(--ds-side-navigation-item-padding-x);
      border-radius:
        0
        var(--ds-side-navigation-item-radius)
        var(--ds-side-navigation-item-radius)
        0;
      font-weight: var(--ds-side-navigation-font-weight);
    }

    .ds-side-navigation__subitem {
      min-height: var(--ds-side-navigation-submenu-item-min-height);
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: var(--ds-side-navigation-submenu-item-gap);
      padding:
        var(--ds-side-navigation-submenu-padding-y)
        var(--ds-side-navigation-submenu-padding-x);
      border-radius:
        0
        var(--ds-side-navigation-item-radius)
        var(--ds-side-navigation-item-radius)
        0;
      font-weight: 400;
    }

    .ds-side-navigation__item:hover:not(:disabled):not(.ds-side-navigation__item--disabled):not(.ds-side-navigation__item--active):not(.ds-side-navigation__item--expanded),
    .ds-side-navigation__subitem:hover:not(:disabled):not(.ds-side-navigation__subitem--disabled):not(.ds-side-navigation__subitem--selected) {
      background: var(--ds-side-navigation-hover-background);
    }

    .ds-side-navigation__item--active,
    .ds-side-navigation__subitem--selected {
      border-radius:
        0
        var(--ds-side-navigation-selected-radius)
        var(--ds-side-navigation-selected-radius)
        0;
      background: var(--ds-side-navigation-selected-background);
      color: var(--ds-side-navigation-selected-content-color);
    }

    .ds-side-navigation__item--expanded {
      color: var(--ds-side-navigation-selected-content-color);
    }

    .ds-side-navigation__item--active::before,
    .ds-side-navigation__item--expanded::before,
    .ds-side-navigation__subitem::before {
      content: '';
      position: absolute;
      inset-block: 0;
      left: 0;
    }

    .ds-side-navigation__subitem::before {
      width: var(--ds-side-navigation-submenu-divider-width);
      background: var(--ds-side-navigation-submenu-divider-color);
    }

    .ds-side-navigation__item--active::before,
    .ds-side-navigation__item--expanded::before,
    .ds-side-navigation__subitem--selected::before {
      width: var(--ds-side-navigation-selected-indicator-width);
      background: var(--ds-side-navigation-selected-content-color);
    }

    .ds-side-navigation__item:focus-visible,
    .ds-side-navigation__subitem:focus-visible {
      z-index: 1;
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: calc(-1 * var(--ds-focus-stroke));
    }

    .ds-side-navigation__item:disabled,
    .ds-side-navigation__subitem:disabled,
    .ds-side-navigation__item--disabled,
    .ds-side-navigation__subitem--disabled {
      color: var(--ds-side-navigation-disabled-content-color);
      cursor: not-allowed;
      opacity: calc(var(--ds-side-navigation-disabled-opacity) / 100);
    }

    .ds-side-navigation__leading-icon,
    .ds-side-navigation__chevron {
      flex: 0 0 auto;
      color: currentColor;
    }

    .ds-side-navigation__label {
      min-width: 0;
      flex: 1 1 auto;
      overflow: visible;
      overflow-wrap: anywhere;
      text-overflow: clip;
      white-space: normal;
    }

    .ds-side-navigation__subitem .ds-side-navigation__label {
      width: 100%;
      flex: 0 0 auto;
    }

    .ds-side-navigation__item ds-tag-pill,
    .ds-side-navigation__subitem ds-tag-pill {
      flex: 0 0 auto;
    }

    .ds-side-navigation__submenu {
      margin-left: var(--ds-side-navigation-submenu-indent);
      padding-top: var(--ds-side-navigation-submenu-container-padding-y);
      padding-bottom: var(--ds-side-navigation-submenu-container-padding-y);
    }

    .ds-side-navigation__contact-divider {
      width: 100%;
      height: var(--ds-side-navigation-submenu-divider-width);
      margin: var(--ds-side-navigation-item-gap) 0 0;
      flex: 0 0 auto;
      border: 0;
      background: var(--ds-side-navigation-submenu-divider-color);
    }

    .ds-side-navigation__contact-link {
      flex: 0 0 auto;
      margin-top: var(--ds-side-navigation-item-gap);
      color: var(--ds-side-navigation-selected-content-color);
    }

    .ds-side-navigation__contact {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      gap: var(--ds-side-navigation-item-gap);
      padding: 0 var(--ds-side-navigation-contact-content-padding-x) var(--ds-side-navigation-contact-content-padding-bottom);
    }

    .ds-side-navigation__contact-panel {
      container: ds-header / inline-size;
      flex: 0 0 auto;
      width: 100%;
    }

    .ds-side-navigation__footer {
      margin-top: var(--ds-side-navigation-footer-gap);
      padding-left: var(--ds-side-navigation-footer-padding-start);
    }

    .ds-side-navigation__footer:empty {
      display: none;
    }

    .ds-text--visual-padding-none {
      text-box-trim: trim-both;
    }

    @media (prefers-reduced-motion: reduce) {
      .ds-side-navigation-shell--mobile {
        transition-delay: 0s;
      }

      .ds-side-navigation__backdrop,
      .ds-side-navigation-shell--mobile .ds-side-navigation__panel {
        transition-duration: 0.01ms;
      }
    }
  `],
})
export class DsSideNavigationComponent implements OnChanges {
  private static nextId = 0;
  private readonly instanceId = DsSideNavigationComponent.nextId++;
  private manuallyCollapsedSelectedValue: string | null = null;

  @ContentChildren(DsAccordionComponent, { descendants: true })
  private footerAccordions?: QueryList<DsAccordionComponent>;

  @ContentChild(DsSideNavigationContactContentDirective)
  contactContent?: DsSideNavigationContactContentDirective;

  @Input() items: readonly DsSideNavigationItem[] = [];
  @Input() value = '';
  @Input() expandedValue: string | null = null;
  @Input() disabled = false;
  @Input() breakpoint: DsSideNavigationBreakpoint = 'desktop';
  @Input() open = false;
  @Input() closeOnSelection = true;
  @Input() ariaLabel = 'Navegación principal';
  @Input() mobileTitle = 'Menú';
  @Input() closeAriaLabel = 'Cerrar menú';
  @Input({ transform: booleanAttribute }) showContact = false;
  @Input() contactLabel = 'Contáctanos';
  @Input() contactAriaLabel = 'Contáctanos';
  @Input() contactPanelTitle = 'Contáctanos';
  @Input() contactBackAriaLabel = 'Volver al menú';
  @Input({ transform: booleanAttribute }) contactOpen = false;

  @Output() valueChange = new EventEmitter<string>();
  @Output() expandedValueChange = new EventEmitter<string | null>();
  @Output() openChange = new EventEmitter<boolean>();
  @Output() itemSelected = new EventEmitter<DsSideNavigationSelection>();
  @Output() closed = new EventEmitter<void>();
  @Output() contactOpenChange = new EventEmitter<boolean>();
  @Output() contactClicked = new EventEmitter<void>();

  readonly contactTemplateContext: DsSideNavigationContactTemplateContext = {
    $implicit: { close: () => this.closeContact() },
    close: () => this.closeContact(),
  };

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    const valueChange = changes['value'];
    if (valueChange && valueChange.previousValue !== valueChange.currentValue) {
      this.manuallyCollapsedSelectedValue = null;
    }

    if (!this.isMobile) return;

    if (changes['open'] && !changes['open'].firstChange) {
      this.scheduleMenuReset(!this.open);
    }

    if (changes['contactOpen']?.currentValue === true) {
      this.scheduleMenuReset();
    }
  }

  @HostBinding('class.ds-side-navigation-host--mobile')
  get mobileHost(): boolean {
    return this.isMobile;
  }

  get isMobile(): boolean {
    return this.breakpoint === 'mobile';
  }

  hasChildren(item: DsSideNavigationItem): boolean {
    return Boolean(item.children?.length);
  }

  isExpanded(item: DsSideNavigationItem): boolean {
    return this.expandedValue === item.value
      || (this.isSelectedGroup(item) && this.manuallyCollapsedSelectedValue !== item.value);
  }

  isCurrentItem(item: DsSideNavigationItem): boolean {
    return !this.disabled && !item.disabled && !this.hasChildren(item) && item.value === this.value;
  }

  isItemActive(item: DsSideNavigationItem): boolean {
    return this.isExpanded(item) || this.isItemSelected(item);
  }

  private isItemSelected(item: DsSideNavigationItem): boolean {
    return item.value === this.value
      || Boolean(item.children?.some(child => child.value === this.value));
  }

  private isSelectedGroup(item: DsSideNavigationItem): boolean {
    return this.hasChildren(item) && this.isItemSelected(item);
  }

  submenuId(item: DsSideNavigationItem): string {
    const safeValue = item.value.replace(/[^a-zA-Z0-9_-]/g, '-');
    return `ds-side-navigation-${this.instanceId}-${safeValue}`;
  }

  linkRel(target: DsSideNavigationTarget | undefined): string | null {
    return target === '_blank' ? 'noopener noreferrer' : null;
  }

  selectItem(item: DsSideNavigationItem): void {
    if (this.disabled || item.disabled) return;

    if (this.hasChildren(item)) {
      if (this.isSelectedGroup(item)) {
        if (this.isExpanded(item)) {
          this.manuallyCollapsedSelectedValue = item.value;
          if (this.expandedValue === item.value) {
            this.expandedValueChange.emit(null);
          }
        } else {
          this.manuallyCollapsedSelectedValue = null;
          this.expandedValueChange.emit(item.value);
        }
        return;
      }

      this.expandedValueChange.emit(this.expandedValue === item.value ? null : item.value);
      return;
    }

    this.emitSelection(item);
  }

  selectChild(item: DsSideNavigationItem, child: DsSideNavigationChild): void {
    if (this.disabled || item.disabled || child.disabled) return;
    this.emitSelection(child);
  }

  requestClose(): void {
    if (!this.isMobile || !this.open) return;
    this.resetMenuContent();
    this.resetContactLayer();
    this.openChange.emit(false);
    this.closed.emit();
  }

  openContact(): void {
    if (this.disabled) return;
    this.resetMenuContent();
    this.contactOpen = true;
    this.contactOpenChange.emit(true);
    this.contactClicked.emit();
  }

  closeContact(): void {
    this.contactOpen = false;
    this.contactOpenChange.emit(false);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isMobile && this.open) {
      event.preventDefault();
      this.requestClose();
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target?.matches('.ds-side-navigation__item, .ds-side-navigation__subitem')) return;

    const destinations = this.enabledDestinations;
    const currentIndex = destinations.indexOf(target);
    if (currentIndex < 0) return;

    let nextIndex: number | null = null;
    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % destinations.length;
    if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + destinations.length) % destinations.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = destinations.length - 1;

    if (nextIndex !== null) {
      event.preventDefault();
      destinations[nextIndex]?.focus();
      return;
    }

    const value = target.dataset['dsSideNavigationValue'];
    const item = this.items.find(candidate => candidate.value === value);
    if (!item || !this.hasChildren(item)) return;

    if (event.key === 'ArrowRight' && !this.isExpanded(item)) {
      event.preventDefault();
      this.selectItem(item);
    }

    if (event.key === 'ArrowLeft' && this.isExpanded(item)) {
      event.preventDefault();
      this.selectItem(item);
    }
  }

  private get enabledDestinations(): HTMLElement[] {
    return Array.from(
      this.elementRef.nativeElement.querySelectorAll<HTMLElement>(
        '.ds-side-navigation__item:not(:disabled):not([aria-disabled="true"]), .ds-side-navigation__subitem:not(:disabled):not([aria-disabled="true"])',
      ),
    );
  }

  private resetMenuContent(): void {
    if (this.expandedValue !== null) {
      this.expandedValue = null;
      this.expandedValueChange.emit(null);
    }
    this.footerAccordions?.forEach((accordion) => accordion.collapse());
  }

  private resetContactLayer(): void {
    if (!this.contactOpen) return;
    this.contactOpen = false;
    this.contactOpenChange.emit(false);
  }

  private scheduleMenuReset(resetContact = false): void {
    // Input-driven resets must emit after Angular finishes checking the
    // current bindings. Microtasks still run before the browser paints.
    void Promise.resolve().then(() => {
      this.resetMenuContent();
      if (resetContact) this.resetContactLayer();
    });
  }

  private emitSelection(selection: DsSideNavigationSelection): void {
    this.valueChange.emit(selection.value);
    this.itemSelected.emit(selection);

    if (this.isMobile && this.closeOnSelection) {
      this.requestClose();
    }
  }
}
